import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {
    Credential,
    type IModel,
    type ITridentResource,
    LakehouseLinkedService,
    ReferenceAdapter,
    azureDefaultAuthRefTypes,
    lakehouseAuthTypes
} from 'shell-models-lib';
import {
    akvToResource,
    akvToModel,
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase
} from './azure-key-vault.util';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';
import { GlobalVariables } from 'app/services/globals';
import { isCIEnvironment, isDevEnvironment, isStagingEnvironment } from 'environments/environment.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(LakehouseLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            linkedServiceName: '*',
            typeProperties: {
                workspaceId: '*',
                artifactId: '*',
                rootFolder: __IS_FABRIC_DPE__ ? '*' : undefined,
                tenant: __IS_FABRIC_DPE__ ? undefined : '*',
                servicePrincipalId: __IS_FABRIC_DPE__ ? undefined : '*',
                servicePrincipalKey: __IS_FABRIC_DPE__ ? undefined : '*',
                servicePrincipalCredentialType: __IS_FABRIC_DPE__ ? undefined : '*',
                servicePrincipalCredential: __IS_FABRIC_DPE__ ? undefined : 'secret',
                encryptedCredential: __IS_FABRIC_DPE__ ? undefined : '*',
                credential: __IS_FABRIC_DPE__ ? undefined : '*',
                fabricEnvironment: __IS_FABRIC_DPE__ ? undefined : '*',
                tridentDefaultSchema: __IS_FABRIC_DPE__ ? '*' : undefined, // Only used in trident as a temporary value which is not saved in backend
                endpoint: __IS_FABRIC_DPE__ ? '*' : undefined,
                authenticationType: !__IS_FABRIC_DPE__ ? '*' : undefined
            },
            parameters: '*'
        }
    }
})
export class LakehouseLinkedServiceAdapter extends LinkedServiceAdapter<LakehouseLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Pick<
        Model.LakehouseLinkedServiceTypeProperties,
        'workspaceId' | 'artifactId' | 'tridentDefaultSchema' | 'endpoint'
    > => {
        let endpoint: string;
        try {
            endpoint = JSON.parse(tridentResource.extendedProperties?.DwProperties as string)?.tdsEndpoint;
        } catch (e) {}
        return {
            workspaceId: tridentResource.workspaceObjectId,
            artifactId: tridentResource.objectId,
            tridentDefaultSchema: tridentResource.extendedProperties?.DefaultSchema,
            endpoint
        };
    };

    protected modelConstructor(): new () => LakehouseLinkedService {
        return LakehouseLinkedService;
    }

    protected model(): IModel {
        return LakehouseLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: LakehouseLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.LakehouseLinkedService;
        properties.type = Metamodel.modelFor(LakehouseLinkedService).name;

        const sharedTypeProperties = {
            workspaceId: this.valueToResource(linkedService, LakehouseLinkedService.WORKSPACE_ID),
            artifactId: this.valueToResource(linkedService, LakehouseLinkedService.ARTIFACT_ID)
        };

        if (__IS_FABRIC_DPE__) {
            properties.typeProperties = {
                ...sharedTypeProperties,
                /**
                 * Usually in Trident, we don't need to set the `rootFolder` because it decides by dataset. But in copy
                 * assistant, we don't generate dataset resources from the dataset model but from linkedService and properties.
                 * So in `toModel`, we don't set `rootFolder` but in `toResource`, if there is `rootFolder`, we set it.
                 */
                rootFolder: this.valueToResource(linkedService, LakehouseLinkedService.ROOT_FOLDER)
            };
        } else {
            const isSP = linkedService.authenticationMethod === lakehouseAuthTypes.ServicePrincipalAuthentication;
            const isInlineSP =
                isSP && linkedService.authReferenceMethod === azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
            properties.typeProperties = {
                ...sharedTypeProperties,
                authenticationType: linkedService.authenticationMethod,
                tenant: isInlineSP ? this.valueToResource(linkedService, LakehouseLinkedService.TENANT) : undefined,
                servicePrincipalId: isInlineSP
                    ? this.valueToResource(linkedService, LakehouseLinkedService.SERVICE_PRINCIPAL_ID)
                    : undefined,
                servicePrincipalCredentialType: isInlineSP ? linkedService.servicePrincipalCredentialType : undefined,
                servicePrincipalCredential: isInlineSP
                    ? linkedService.servicePrincipalCredentialType ===
                          servicePrincipalCredentialTypes.ServicePrincipalCert && linkedService.servicePrincipalCert
                        ? akvToResource(linkedService.servicePrincipalCert, this.context)
                        : parameterizablePasswordOrAKVtoSecretBase(
                              linkedService,
                              LakehouseLinkedService.SERVICE_PRINCIPAL_KEY,
                              this.context
                          )
                    : undefined,
                credential:
                    linkedService.credential &&
                    ((isSP && !isInlineSP) ||
                        linkedService.authenticationMethod === lakehouseAuthTypes.UserAssignedManagedIdentity ||
                        linkedService.authenticationMethod === lakehouseAuthTypes.UserAuth ||
                        (GlobalVariables.isSamiSupportEnabled &&
                            linkedService.authenticationMethod === lakehouseAuthTypes.MSIAuthentication))
                        ? (ReferenceAdapter.toResource(
                              linkedService.credential,
                              this.context
                          ) as Model.CredentialReference)
                        : undefined,
                fabricEnvironment:
                    linkedService.fabricEnvironment ||
                    (isDevEnvironment() || isStagingEnvironment()
                        ? 'DXT'
                        : isCIEnvironment()
                          ? 'Daily'
                          : GlobalVariables.isMicrosoftInternal
                            ? 'MSIT'
                            : undefined)
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: LakehouseLinkedService
    ): Promise<LakehouseLinkedService> {
        const properties = linkedServiceResource.properties as Model.LakehouseLinkedService;
        const typeProperties: Model.LakehouseLinkedServiceTypeProperties = properties.typeProperties;

        linkedService.reset();

        if (typeProperties.authenticationType) {
            linkedService.authenticationMethod = typeProperties.authenticationType;
        }

        ValueAdapter.toModel(linkedService, LakehouseLinkedService.WORKSPACE_ID, typeProperties.workspaceId);
        ValueAdapter.toModel(linkedService, LakehouseLinkedService.ARTIFACT_ID, typeProperties.artifactId);
        linkedService.defaultSchema = !__IS_FABRIC_DPE__ ? 'dbo' : typeProperties.tridentDefaultSchema;
        if (properties.typeProperties.servicePrincipalId) {
            linkedService.authenticationMethod = lakehouseAuthTypes.ServicePrincipalAuthentication;
            ValueAdapter.toModel(
                linkedService,
                LakehouseLinkedService.SERVICE_PRINCIPAL_ID,
                properties.typeProperties.servicePrincipalId
            );
            if (properties.typeProperties.servicePrincipalCredentialType != null) {
                linkedService.servicePrincipalCredentialType = properties.typeProperties.servicePrincipalCredentialType;
                if (
                    linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalKey
                ) {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        LakehouseLinkedService.SERVICE_PRINCIPAL_KEY,
                        properties.typeProperties.servicePrincipalCredential,
                        this.context
                    );
                } else if (
                    linkedService.servicePrincipalCredentialType ===
                    servicePrincipalCredentialTypes.ServicePrincipalCert
                ) {
                    linkedService.servicePrincipalCert = akvToModel(
                        properties.typeProperties.servicePrincipalCredential as Model.SecretBase,
                        this.context
                    );
                }
            } else {
                parameterizableSecretBaseToModel(
                    linkedService,
                    LakehouseLinkedService.SERVICE_PRINCIPAL_KEY,
                    properties.typeProperties.servicePrincipalKey,
                    this.context
                );
            }
            ValueAdapter.toModel(linkedService, LakehouseLinkedService.TENANT, properties.typeProperties.tenant);
            linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
        } else if (properties.typeProperties.credential) {
            linkedService.credential = ReferenceAdapter.toProxyModel(
                Metamodel.modelFor(Credential),
                properties.typeProperties.credential,
                this.context
            );
            linkedService.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;
        } else {
            linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
        }

        linkedService.fabricEnvironment = properties.typeProperties.fabricEnvironment;
        linkedService.sqlAnalyticsEndpoint = properties.typeProperties.endpoint;

        return linkedService;
    }
}
